import type { FC } from 'react';
import { useMemo } from 'react';
import type { LayoutChangeEvent } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { Text, TouchableRipple } from 'react-native-paper';

import { TreesIcon } from '@/assets';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

export const REGION_LOCATION_BAR_HEIGHT = 60;

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    bar: {
      minHeight: REGION_LOCATION_BAR_HEIGHT,
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 12,
      backgroundColor: theme.colors.surface
    },
    loc: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingLeft: 4
    },
    locPressable: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 4,
      paddingVertical: 4,
      borderRadius: 8
    },
    text: { flex: 1 },
    name: {
      fontFamily: 'Montserrat_500Medium',
      fontSize: 22,
      lineHeight: 28,
      color: theme.colors.onSurface
    },
    sub: {
      fontSize: 14,
      lineHeight: 20,
      fontWeight: '400',
      letterSpacing: 0.25,
      marginTop: 2,
      color: theme.colors.onSurfaceVariant
    }
  });

export const RegionLocationBar: FC<{
  region?: string;
  subtitle?: string;
  onPress?: () => void;
  onLayout?: (event: LayoutChangeEvent) => void;
}> = ({ region, subtitle, onPress, onLayout }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const content = (
    <>
      <TreesIcon color={theme.colors.primary} width={28} height={28} />
      <View style={styles.text}>
        {region && (
          <Text style={styles.name} numberOfLines={2} ellipsizeMode="tail">
            {region}
          </Text>
        )}
        {subtitle && <Text style={styles.sub}>{subtitle}</Text>}
      </View>
    </>
  );

  return (
    <View style={styles.bar} onLayout={onLayout}>
      {onPress ? (
        <TouchableRipple
          testID="meine-region-location-button"
          onPress={onPress}
          rippleColor={theme.rippleColors.onSurface}
          borderless
          style={styles.locPressable}
        >
          <View style={styles.loc}>{content}</View>
        </TouchableRipple>
      ) : (
        <View style={styles.loc}>{content}</View>
      )}
    </View>
  );
};
